import { useMemo } from "react";
import { ArrowDown2, SearchNormal } from "iconsax-reactjs";
import { Avatar, AvatarFallback, Checkbox, Input } from "../../../../shared/ui";

export type MemberOption = {
  id: string;
  name: string;
  email: string;
  label: string;
};

type MemberSelectionDropdownProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: MemberOption[];
  selectedMemberId: string;
  onSelectMember: (memberId: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
};

const MemberSelectionDropdown = ({
  open,
  onOpenChange,
  members,
  selectedMemberId,
  onSelectMember,
  query,
  onQueryChange,
  placeholder = "Select member",
}: MemberSelectionDropdownProps) => {
  const selectedMemberName =
    members.find((member) => member.id === selectedMemberId)?.name ?? "";

  const filteredMembers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return members;
    return members.filter((member) => {
      const nameMatch = member.name.toLowerCase().includes(normalized);
      const emailMatch = member.email.toLowerCase().includes(normalized);
      return nameMatch || emailMatch;
    });
  }, [members, query]);

  return (
    <div>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex h-14 w-full items-center justify-between rounded-[16px] bg-[#E9ECFA] px-5 text-left"
      >
        <span className="truncate text-[16px] font-[400] text-[#737AA8]">
          {selectedMemberName || placeholder}
        </span>
        <ArrowDown2
          size={20}
          className={`text-[#737AA8] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="mt-3 rounded-[16px] border border-[#E1E6FF] bg-white p-4">
          <Input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search members"
            startIcon={<SearchNormal size={18} className="text-[#737AA8]" />}
            className="h-12 rounded-[12px] bg-[#E9ECFA]"
          />

          <div className="mt-3 max-h-[220px] space-y-1 overflow-y-auto pr-1">
            {filteredMembers.length === 0 ? (
              <div className="rounded-[10px] px-2 py-3 text-[14px] text-[#6B6F8A]">
                No members found.
              </div>
            ) : (
              filteredMembers.map((member) => {
                const isSelected = selectedMemberId === member.id;
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => {
                      onSelectMember(member.id);
                      onOpenChange(false);
                    }}
                    className="flex w-full items-center justify-between rounded-[10px] px-2 py-2 text-left hover:bg-[#F7F8FD]"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="size-9 bg-[#EEF1FB]">
                        <AvatarFallback className="bg-[#EEF1FB] text-[14px] font-[500] text-[#3B4060]">
                          {member.label}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate text-[16px] font-[500] text-[#161827]">
                          {member.name}
                        </div>
                        <div className="truncate text-[13px] text-[#6E7397]">
                          {member.email}
                        </div>
                      </div>
                    </div>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => {
                        onSelectMember(member.id);
                        onOpenChange(false);
                      }}
                      size="sm"
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MemberSelectionDropdown;
